import AddOutlined from "@mui/icons-material/AddOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";
import { useEmployeeTerminology } from "../../utils/terminology";
import DepartmentFormDialog from "./dialogs/DepartmentFormDialog";

export interface Department {
  id: string;
  companyId: string;
  name: string;
  description?: string;
  color?: string;
  employeeCount?: number;
  userCount?: number;
  created_date?: string;
  updated_date?: string;
}

export default ({
  company,
  companyId,
  getToken,
  currentUser,
  eventListener,
  openDialog,
  userRole,
}: {
  company: Company;
  companyId: string;
  getToken: () => Promise<string>;
  currentUser: any | User;
  eventListener: (event: any) => void;
  openDialog: (title: string, content: string, buttonText: string, onClick: () => void) => void;
  userRole?: string | null;
}) => {
  const { employees: employeesText, employeesLower } = useEmployeeTerminology();
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);

  const getData = async () => {
    const currentToken = await getToken();
    setLoading(true);

    await axios
      .get(`${hostname}/departments?companyId=${companyId}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        // Ensure we always set an array
        setDepartments(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err: any) => {
        // Set empty array on error to prevent map errors
        setDepartments([]);
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!init && companyId) {
      setInit(true);
      getData();
    }
  }, [companyId, init]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, department: Department) => {
    setAnchorEl(event.currentTarget);
    setSelectedDepartment(department);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDepartment(null);
  };

  const handleAddDepartment = () => {
    setEditingDepartment(null);
    setFormDialogOpen(true);
  };

  const handleEditDepartment = () => {
    setEditingDepartment(selectedDepartment);
    setFormDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteDepartment = async () => {
    if (!selectedDepartment) return;

    const departmentToDelete = selectedDepartment;
    handleMenuClose();

    openDialog(
      "Delete Department",
      `Are you sure you want to delete "${departmentToDelete.name}"? ${employeesText} and users will no longer be assigned to this department.`,
      "Delete",
      async () => {
        const currentToken = await getToken();
        await axios
          .delete(`${hostname}/departments/${departmentToDelete.id}?companyId=${companyId}`, {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          })
          .then(() => {
            eventListener({
              type: "SET_SUCCESS",
              message: "Department deleted successfully",
            });
            getData();
          })
          .catch((err: any) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          });
      }
    );
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setEditingDepartment(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    getData();
  };

  // Only admins and owners can manage departments
  const canManage = userRole === "admin" || userRole === "owner";

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Departments</Typography>
        {canManage && (
          <Button
            type="button"
            variant="contained"
            startIcon={<AddOutlined />}
            onClick={handleAddDepartment}
          >
            Add Department
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Organize your AI {employeesLower} into departments and control which collaborators can access them.
        By default, admins and owners can see all {employeesLower}. Members with no departments assigned can see all {employeesLower}.
        Members with departments assigned can only see {employeesLower} in those departments.
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">{employeesText}</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!departments || departments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      No departments yet. Create your first department to start organizing your team.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                departments.map((department) => (
                  <TableRow key={department.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {department.color && (
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: department.color,
                            }}
                          >
                            {" "}
                          </Avatar>
                        )}
                        <Typography variant="body1">{department.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {department.description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={department.employeeCount || 0} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={department.userCount || 0} size="small" />
                    </TableCell>
                    <TableCell align="right">
                      {canManage && (
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, department)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditDepartment}>
          <EditOutlined fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteDepartment}>
          <DeleteOutline fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      <DepartmentFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        department={editingDepartment}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
    </Box>
  );
};
